import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import * as Notifications from 'expo-notifications';
import gql from 'graphql-tag';

import { CHECKLIST_SIGN_OFF_PUSH_TYPE } from '../../../../common/constants';
import { useUserContext } from '../../../../common/utils/context';
import registerForPushNotificationsAsync from './registerForPushNotificationsAsync';

// Opens the page a tapped push points to. Other Sentry pushes keep opening the app only.
const openPushNotificationTarget = (navigation, data) => {
	if (data?.type === CHECKLIST_SIGN_OFF_PUSH_TYPE && data.checklistSubmissionId) {
		navigation.navigate('MainStack', {
			screen: 'ChecklistSignOffView',
			params: { checklistSubmissionId: data.checklistSubmissionId },
		});
	}
};

const PushNotificationProvider = ({ children }) => {
	const { user } = useUserContext();
	const [setPushNotificationToken] = useMutation(SET_PUSH_NOTIFICATION_TOKEN);
	const navigation = useNavigation();

	useEffect(() => {
		let cancelled = false;

		if (!user?.id) {
			return undefined;
		}

		registerForPushNotificationsAsync()
			.then(token => {
				if (!token || cancelled) {
					return null;
				}

				return setPushNotificationToken({
					variables: {
						userId: user.id,
						token,
					},
				});
			})
			.catch(error => {
				console.warn('Failed to save push notification token', error);
			});

		return () => {
			cancelled = true;
		};
	}, [setPushNotificationToken, user?.id]);

	// Listen before reading the tap that opened the app, so no tap falls between the two.
	useEffect(() => {
		if (Platform.OS === 'web') {
			return undefined;
		}

		const handleNotificationResponse = (response) => {
			openPushNotificationTarget(navigation, response?.notification?.request?.content?.data);
			try {
				Notifications.clearLastNotificationResponse();
			}
			catch (error) {
				console.warn('Failed to clear handled notification response', error);
			}
		};

		const subscription = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);
		const initialResponse = Notifications.getLastNotificationResponse();
		if (initialResponse) {
			handleNotificationResponse(initialResponse);
		}

		return () => subscription.remove();
	}, [navigation]);

	return children;
};

const SET_PUSH_NOTIFICATION_TOKEN = gql`
	mutation setPushNotificationToken($userId: ID, $token: String!) {
		setPushNotificationToken(userId: $userId, token: $token) {
			status
		}
	}
`;

export default PushNotificationProvider;
